import { CustomerBranch } from './customer-branch.util';

export interface CustomerResponsible {
  id?: string;
  draftId?: string;
  enterpriseId?: string | null;
  branchTitle?: string;
  responsibleName: string;
  responsibleCpf: string;
  responsibleRg: string;
  responsibleNationality: string;
  responsiblePhone: string;
  responsibleEmail: string;
  responsibleBirthDate: string;
  responsibleZipCode: string;
  responsibleState: string;
  responsibleCity: string;
  responsibleAddress: string;
  responsibleNumber: string;
  responsibleNeighborhood: string;
  responsibleComplement: string;
}

function text(value: unknown): string {
  return value == null ? '' : String(value);
}

export function emptyResponsible(partial: Partial<CustomerResponsible> = {}): CustomerResponsible {
  return {
    id: partial.id,
    draftId: partial.draftId,
    enterpriseId: partial.enterpriseId ?? null,
    branchTitle: partial.branchTitle || '',
    responsibleName: text(partial.responsibleName),
    responsibleCpf: text(partial.responsibleCpf),
    responsibleRg: text(partial.responsibleRg),
    responsibleNationality: text(partial.responsibleNationality || 'Brasileiro'),
    responsiblePhone: text(partial.responsiblePhone),
    responsibleEmail: text(partial.responsibleEmail),
    responsibleBirthDate: text(partial.responsibleBirthDate),
    responsibleZipCode: text(partial.responsibleZipCode),
    responsibleState: text(partial.responsibleState),
    responsibleCity: text(partial.responsibleCity),
    responsibleAddress: text(partial.responsibleAddress),
    responsibleNumber: text(partial.responsibleNumber),
    responsibleNeighborhood: text(partial.responsibleNeighborhood),
    responsibleComplement: text(partial.responsibleComplement),
  };
}

export function toCustomerResponsible(source: any): CustomerResponsible {
  return emptyResponsible({
    ...source,
    enterpriseId: source?.enterpriseId || source?.enterprise?.id || null,
    branchTitle: source?.branchTitle || source?.enterprise?.title || '',
  });
}

export function responsibleHasInfo(responsible?: Partial<CustomerResponsible> | null): boolean {
  if (!responsible) return false;
  return [
    responsible.responsibleName,
    responsible.responsibleCpf,
    responsible.responsibleEmail,
    responsible.responsiblePhone,
  ].some((value) => text(value).trim().length > 0);
}

export function responsibleBranchLabel(
  responsible: CustomerResponsible,
  branches: CustomerBranch[]
): string {
  if (responsible.branchTitle) {
    return responsible.branchTitle;
  }
  const match = branches.find(
    (branch) =>
      (branch.id && branch.id === responsible.enterpriseId) ||
      branch.localKey === responsible.enterpriseId
  );
  if (!match) return '—';
  return match.branchIsMain ? `${match.branchTitle} (Matriz)` : match.branchTitle;
}

export function filterResponsibles(
  rows: CustomerResponsible[],
  term: string
): CustomerResponsible[] {
  const query = (term || '').trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) =>
    [row.responsibleName, row.responsibleCpf, row.responsibleEmail, row.responsiblePhone, row.branchTitle, row.responsibleCity]
      .join(' ')
      .toLowerCase()
      .includes(query)
  );
}

export function upsertResponsible(
  rows: CustomerResponsible[],
  responsible: CustomerResponsible
): CustomerResponsible[] {
  const key = responsible.id || responsible.draftId;
  const index = rows.findIndex((row) => key && (row.id === key || row.draftId === key));
  if (index < 0) return [...rows, responsible];
  return rows.map((row, rowIndex) => (rowIndex === index ? responsible : row));
}

export function removeResponsible(
  rows: CustomerResponsible[],
  responsible: CustomerResponsible
): CustomerResponsible[] {
  const key = responsible.id || responsible.draftId;
  return rows.filter((row) => (row.id || row.draftId) !== key);
}

export function responsibleCityLabel(responsible: CustomerResponsible): string {
  return [responsible.responsibleCity, responsible.responsibleState].filter(Boolean).join(' / ') || '—';
}
